import React from 'react';
import { 
  Zap, 
  AlignVerticalJustifyCenter, 
  FileText, 
  GraduationCap, 
  Globe, 
  ChevronDown, 
  ChevronLeft, 
  ChevronRight, 
  RotateCw, 
  Copy, 
  Check 
} from 'lucide-react';
import { LANGUAGES } from '../types';

interface AIAssistantMenuProps {
  isDark: boolean;
  versionIndex: number;
  historyCount: number;
  onNavigateHistory: (index: number) => void;
  onPostProcess: (action: "optimize" | "pro" | "translate" | "structure" | "narrative", lang?: string) => void;
  isProcessing: boolean;
  onRestoreOriginal?: () => void;
  onCopy?: () => void;
  showCopy?: boolean;
}

const AIAssistantMenu: React.FC<AIAssistantMenuProps> = ({
  isDark,
  versionIndex,
  historyCount,
  onNavigateHistory,
  onPostProcess,
  isProcessing,
  onRestoreOriginal,
  onCopy,
  showCopy = true
}) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    if (onCopy) {
      onCopy();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex items-center flex-wrap gap-1.5">
      {/* Navegación por versiones: Estilo unificado con Transcripción IA */}
      {historyCount > 1 && (
        <div className="flex items-center space-x-1 mr-2 border-r pr-2 border-gray-300 dark:border-gray-600">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (versionIndex > 0) onNavigateHistory(versionIndex - 1);
            }}
            disabled={versionIndex === 0}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-20"
            title="Versión Anterior"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center justify-center min-w-[24px]">
            <span className="text-[10px] font-black text-blue-600">
              {versionIndex + 1}
            </span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (versionIndex < historyCount - 1) onNavigateHistory(versionIndex + 1);
            }}
            disabled={versionIndex >= historyCount - 1}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-20"
            title="Versión Siguiente"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Habilidades de Procesamiento */}
      <button
        onClick={(e) => { e.stopPropagation(); onPostProcess("optimize"); }}
        disabled={isProcessing}
        className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase flex items-center gap-1.5 transition-all active:scale-95 ${isDark ? "bg-amber-600/20 text-amber-400 hover:bg-amber-600/30" : "bg-amber-100 text-amber-700 hover:bg-amber-200"}`}
      >
        <Zap size={12} />
        <span>Resumir</span>
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onPostProcess("structure"); }}
        disabled={isProcessing}
        className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase flex items-center gap-1.5 transition-all active:scale-95 ${isDark ? "bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30" : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"}`}
      >
        <AlignVerticalJustifyCenter size={12} />
        <span>Estructurar</span>
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onPostProcess("narrative"); }}
        disabled={isProcessing}
        className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase flex items-center gap-1.5 transition-all active:scale-95 ${isDark ? "bg-blue-600/20 text-blue-400 hover:bg-blue-600/30" : "bg-blue-100 text-blue-700 hover:bg-blue-200"}`}
      >
        <FileText size={12} />
        <span>Narrativo</span>
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onPostProcess("pro"); }}
        disabled={isProcessing}
        className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase flex items-center gap-1.5 transition-all active:scale-95 ${isDark ? "bg-purple-600/20 text-purple-400 hover:bg-purple-600/30" : "bg-purple-100 text-purple-700 hover:bg-purple-200"}`}
      >
        <GraduationCap size={12} />
        <span>Refinar PhD</span>
      </button>

      {/* Selector de Idioma */}
      <div className="relative group/lang">
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all ${isDark ? "bg-emerald-600/20 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`} onClick={(e) => e.stopPropagation()}>
          <Globe size={12} />
          <select
            onChange={(e) => { e.stopPropagation(); onPostProcess("translate", e.target.value); }}
            disabled={isProcessing}
            className="bg-transparent text-[9px] font-black uppercase outline-none cursor-pointer appearance-none pr-4"
            defaultValue=""
          >
            <option value="" disabled>Idioma</option>
            {LANGUAGES.map((lang) => (
              <option key={lang} value={lang} className={isDark ? "bg-[#1e2024] text-white" : "bg-white text-slate-900"}>{lang}</option>
            ))}
          </select>
          <ChevronDown size={10} className="absolute right-2 pointer-events-none" />
        </div>
      </div>

      {/* Botón de Copiado */}
      {showCopy && onCopy && (
        <button
          onClick={(e) => { e.stopPropagation(); handleCopy(); }}
          className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase flex items-center gap-1.5 transition-all active:scale-95 ${copied ? "bg-green-500/20 text-green-500" : isDark ? "bg-slate-600/20 text-slate-400 hover:bg-slate-600/30" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          <span>{copied ? "¡Copiado!" : "Copiar"}</span>
        </button>
      )}
    </div>
  );
};

export default AIAssistantMenu;
